import React, { useState, useEffect } from 'react';
import { getUsers } from '../mock/users';
import { createUser, updateUser, deleteUser, changePassword, resetPassword } from '../api/index';

const AdminPanel = ({ onBack, currentUser }) => {
  const [currentUsers, setCurrentUsers] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserRole, setNewUserRole] = useState('standard');
  const [newUserCells, setNewUserCells] = useState([]);
  const [error, setError] = useState('');

  const [editingUser, setEditingUser] = useState(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editCells, setEditCells] = useState([]);
  const [editError, setEditError] = useState('');

  // Estados para cambiar contraseña
  const [changingPasswordUser, setChangingPasswordUser] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Estados para resetear contraseña (solo administradores)
  const [resettingPasswordUser, setResettingPasswordUser] = useState(null);
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');

  useEffect(() => {
    const loadInitialUsers = async () => {
      try {
        const users = await getUsers();
        setCurrentUsers(users);
      } catch (error) {
        console.error('Error cargando usuarios:', error);
        setCurrentUsers([]);
      }
    };
    loadInitialUsers();
  }, []);

  const allCells = [
    'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4'
  ];

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');

    if (!newUserEmail || !newUserPassword || !newUserFirstName || !newUserLastName) {
      setError('Todos los campos son obligatorios (Email, Contraseña, Nombre, Apellido).');
      return;
    }
    if (currentUsers.some(user => user.email === newUserEmail)) {
      setError('Ya existe un usuario con este email.');
      return;
    }

    try {
      const newUser = { 
        email: newUserEmail, 
        password: newUserPassword, 
        firstName: newUserFirstName,
        lastName: newUserLastName,
        role: newUserRole,
        cells: newUserRole === 'admin' ? [] : newUserCells
      };
      await createUser(newUser);
      const updatedUsers = await getUsers();
      setCurrentUsers(updatedUsers);
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserFirstName('');
      setNewUserLastName('');
      setNewUserRole('standard');
      setNewUserCells([]);
      alert('Usuario agregado con éxito.');
    } catch (error) {
      console.error('Error al agregar usuario:', error);
      setError('Error al agregar usuario. Por favor, intente de nuevo.');
    }
  };

  const handleDeleteUser = async (emailToDelete) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar al usuario ${emailToDelete}?`)) {
      try {
        await deleteUser(emailToDelete);
        const updatedUsers = await getUsers();
        setCurrentUsers(updatedUsers);
        alert('Usuario eliminado con éxito.');
      } catch (error) {
        console.error('Error al eliminar usuario:', error);
        alert('Error al eliminar usuario. Por favor, intente de nuevo.');
      }
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditFirstName(user.firstName);
    setEditLastName(user.lastName);
    setEditRole(user.role);
    setEditCells(user.cells || []);
    setEditError('');
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setEditError('');

    if (!editFirstName || !editLastName) {
      setEditError('Nombre y Apellido son obligatorios.');
      return;
    }

    try {
      await updateUser(editingUser.email, {
        firstName: editFirstName,
        lastName: editLastName,
        role: editRole,
        cells: editRole === 'admin' ? [] : editCells
      });
      const updatedUsers = await getUsers();
      setCurrentUsers(updatedUsers);
      setEditingUser(null);
      alert('Usuario actualizado con éxito.');
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      setEditError('Error al actualizar usuario. Por favor, intente de nuevo.');
    }
  };

  const handleChangePasswordClick = (user) => {
    setChangingPasswordUser(user);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Todos los campos son obligatorios.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden.');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }

    try {
      await changePassword(changingPasswordUser.email, currentPassword, newPassword);
      setChangingPasswordUser(null);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      alert('Contraseña cambiada exitosamente.');
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      setPasswordError(error.message || 'Error al cambiar contraseña. Por favor, intente de nuevo.');
    }
  };

  const handleResetPasswordClick = (user) => {
    setResettingPasswordUser(user);
    setResetNewPassword('');
    setResetConfirmPassword('');
    setResetPasswordError('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetPasswordError('');

    if (!resetNewPassword || !resetConfirmPassword) {
      setResetPasswordError('Todos los campos son obligatorios.');
      return;
    }

    if (resetNewPassword !== resetConfirmPassword) {
      setResetPasswordError('Las contraseñas no coinciden.');
      return;
    }

    if (resetNewPassword.length < 6) {
      setResetPasswordError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }

    try {
      await resetPassword(resettingPasswordUser.email, resetNewPassword, currentUser.email);
      setResettingPasswordUser(null);
      setResetNewPassword('');
      setResetConfirmPassword('');
      alert('Contraseña reseteada exitosamente.');
    } catch (error) {
      console.error('Error al resetear contraseña:', error);
      setResetPasswordError(error.message || 'Error al resetear contraseña. Por favor, intente de nuevo.');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-[#0061A8]">Panel de Administración de Usuarios</h2>
        <button 
          onClick={onBack}
          className="text-[#63666A] hover:text-[#0061A8] flex items-center"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver
        </button>
      </div>

      {/* Sección para agregar nuevo usuario */}
      <div className="mb-8 p-4 border border-[#D1D3D4] rounded-lg">
        <h3 className="text-lg font-semibold text-[#0061A8] mb-3">Agregar Nuevo Usuario</h3>
        <form onSubmit={handleAddUser} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Email</label>
            <input
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Contraseña</label>
            <input
              type="password"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Nombre</label>
            <input
              type="text"
              value={newUserFirstName}
              onChange={(e) => setNewUserFirstName(e.target.value)}
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Apellido</label>
            <input
              type="text"
              value={newUserLastName}
              onChange={(e) => setNewUserLastName(e.target.value)}
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A]">Rol</label>
            <select
              value={newUserRole}
              onChange={(e) => setNewUserRole(e.target.value)}
              className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              <option value="standard">Estándar</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          {newUserRole === 'standard' && (
            <div>
              <label className="block text-sm font-medium text-[#63666A] mb-2">Células Asignadas</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-60 overflow-y-auto p-2 border border-[#D1D3D4] rounded-md">
                {allCells.map(cell => (
                  <div key={cell} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`new-cell-${cell}`}
                      value={cell}
                      checked={newUserCells.includes(cell)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewUserCells([...newUserCells, cell]);
                        } else {
                          setNewUserCells(newUserCells.filter(c => c !== cell));
                        }
                      }}
                      className="h-4 w-4 text-[#0061A8] focus:ring-[#0061A8] border-[#D1D3D4] rounded"
                    />
                    <label htmlFor={`new-cell-${cell}`} className="ml-2 text-sm text-[#63666A]">
                      {cell}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <button
            type="submit"
            className="bg-[#0061A8] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
          >
            Agregar Usuario
          </button>
        </form>
      </div>

      {/* Sección para listar usuarios existentes */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-[#0061A8]">Usuarios Existentes</h3>
          <div className="text-sm text-[#63666A]">
            Total: {currentUsers.length} usuarios ({currentUsers.filter(u => u.role === 'admin').length} administradores)
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentUsers.map((user, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#0061A8]">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-medium text-[#0061A8]">{user.firstName} {user.lastName}</h4>
                  <p className="text-sm text-[#63666A]">{user.email}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  user.role === 'admin' 
                    ? 'bg-[#A8D08D] text-white' 
                    : 'bg-[#D1D3D4] text-[#63666A]'
                }`}>
                  {user.role === 'admin' ? 'Administrador' : 'Estándar'}
                </span>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-[#63666A] font-medium mb-1">Células Asignadas:</p>
                <div className="flex flex-wrap gap-1">
                  {user.role === 'admin' ? (
                    <span className="text-xs bg-[#F8F9FA] text-[#63666A] px-2 py-1 rounded">
                      Todas las células
                    </span>
                  ) : user.cells && Array.isArray(user.cells) && user.cells.length > 0 ? (
                    user.cells.map((cell, i) => (
                      <span key={i} className="text-xs bg-[#F8F9FA] text-[#63666A] px-2 py-1 rounded">
                        {cell}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-[#63666A]">Sin células asignadas</span>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                <button
                  onClick={() => handleEditClick(user)}
                  className="text-sm text-[#0061A8] hover:text-[#A8D08D] py-1 px-3 rounded-md border border-[#0061A8] hover:border-[#A8D08D]"
                >
                  Editar
                </button>
                <button
                  onClick={() => handleChangePasswordClick(user)}
                  className="text-sm text-[#A8D08D] hover:text-[#0061A8] py-1 px-3 rounded-md border border-[#A8D08D] hover:border-[#0061A8]"
                >
                  Cambiar Contraseña
                </button>
                {currentUser && currentUser.role === 'admin' && (
                  <button
                    onClick={() => handleResetPasswordClick(user)}
                    className="text-sm text-orange-600 hover:text-orange-800 py-1 px-3 rounded-md border border-orange-600 hover:border-orange-800"
                  >
                    Resetear Contraseña
                  </button>
                )}
                <button
                  onClick={() => handleDeleteUser(user.email)}
                  className="text-sm text-white bg-red-500 hover:bg-red-600 py-1 px-3 rounded-md"
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de edición de usuario */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#0061A8] mb-4">Editar Usuario: {editingUser.email}</h3>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#63666A]">Nombre</label>
                <input
                  type="text"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#63666A]">Apellido</label>
                <input
                  type="text"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#63666A]">Rol</label>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                >
                  <option value="standard">Estándar</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              {editRole === 'standard' && (
                <div>
                  <label className="block text-sm font-medium text-[#63666A] mb-2">Células Asignadas</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-60 overflow-y-auto p-2 border border-[#D1D3D4] rounded-md">
                    {allCells.map(cell => (
                      <div key={cell} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`edit-cell-${cell}`}
                          value={cell}
                          checked={editCells.includes(cell)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditCells([...editCells, cell]);
                            } else {
                              setEditCells(editCells.filter(c => c !== cell));
                            }
                          }}
                          className="h-4 w-4 text-[#0061A8] focus:ring-[#0061A8] border-[#D1D3D4] rounded"
                        />
                        <label htmlFor={`edit-cell-${cell}`} className="ml-2 text-sm text-[#63666A]">
                          {cell}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {editError && <p className="text-red-500 text-sm">{editError}</p>}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="border border-[#D1D3D4] text-[#63666A] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-[#0061A8] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
                >
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para cambiar contraseña */}
      {changingPasswordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-[#0061A8] mb-4">
              Cambiar Contraseña: {changingPasswordUser.firstName} {changingPasswordUser.lastName}
            </h3>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#63666A]">Contraseña Actual</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#63666A]">Nueva Contraseña</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                  required
                  minLength="6"
                />
                <p className="text-xs text-[#63666A] mt-1">Mínimo 6 caracteres</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#63666A]">Confirmar Nueva Contraseña</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
                  required
                />
              </div>
              {passwordError && <p className="text-red-500 text-sm">{passwordError}</p>}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setChangingPasswordUser(null)}
                  className="border border-[#D1D3D4] text-[#63666A] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-[#0061A8] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
                >
                  Cambiar Contraseña
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para resetear contraseña (solo administradores) */}
      {resettingPasswordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-orange-600 mb-4">
              Resetear Contraseña: {resettingPasswordUser.firstName} {resettingPasswordUser.lastName}
            </h3>
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
              <p className="text-sm text-orange-800">
                <strong>⚠️ Función de Administrador:</strong> Puedes establecer una nueva contraseña sin conocer la actual.
              </p>
            </div>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#63666A]">Nueva Contraseña</label>
                <input
                  type="password"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  required
                  minLength="6"
                />
                <p className="text-xs text-[#63666A] mt-1">Mínimo 6 caracteres</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#63666A]">Confirmar Nueva Contraseña</label>
                <input
                  type="password"
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  className="mt-1 block w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  required
                />
              </div>
              {resetPasswordError && <p className="text-red-500 text-sm">{resetPasswordError}</p>}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setResettingPasswordUser(null)}
                  className="border border-[#D1D3D4] text-[#63666A] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-orange-600 text-white py-2 px-4 rounded-md hover:bg-orange-700 transition-colors"
                >
                  Resetear Contraseña
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
